import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Card,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import authService from '../services/auth.service';

export default function ForgotPasswordPage() {
  const theme = useTheme();

  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = identifier.trim();
    if (!clean) {
      setErrorMessage('Please enter your registered email address or username.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      await authService.forgotPassword(clean);
      setSubmitted(true);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        'Unable to send password reset link. Please check your network and try again.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password | Mutual Trust Financial System</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'stretch',
          backgroundColor: 'background.default',
        }}
      >
        {/* Left Side: Security Showcase Panel (Visible on lg/md screens) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { md: '48%', lg: '45%' },
            position: 'relative',
            overflow: 'hidden',
            p: { md: 6, lg: 8 },
            color: '#FFFFFF',
            background: 'linear-gradient(145deg, #071C2E 0%, #0B3B60 55%, #054837 100%)',
            boxShadow: 'inset -1px 0 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Ambient Radial Glows */}
          <Box
            sx={{
              position: 'absolute',
              top: '-15%',
              left: '-15%',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(56, 189, 248, 0.18) 0%, rgba(11, 59, 96, 0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10%',
              right: '-10%',
              width: '450px',
              height: '450px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0) 70%)',
              pointerEvents: 'none',
            }}
          />

          {/* Top Branding Header */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{
                  width: 52,
                  height: 52,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.04em', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', letterSpacing: '0.08em', fontWeight: 600 }}>
                  COOPERATIVE MULTIPURPOSE SOCIETY
                </Typography>
              </Box>
            </Stack>

            <Chip
              label="🔒 Secure Self-Service Account Recovery"
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                mb: 3,
              }}
            />

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.18,
                letterSpacing: '-0.02em',
                mb: 2.5,
                color: '#FFFFFF',
              }}
            >
              Protecting Your Member Account
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.82)',
                lineHeight: 1.6,
                maxWidth: 480,
                fontSize: '1rem',
              }}
            >
              Reset your password securely via our automated verification channel. Links are encrypted and time-bound for maximum asset protection.
            </Typography>
          </Box>

          {/* Recovery Pillars */}
          <Box sx={{ position: 'relative', zIndex: 1, my: 4 }}>
            <Stack spacing={2}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#FFFFFF',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: '10px',
                      bgcolor: 'rgba(56, 189, 248, 0.2)',
                      color: '#38BDF8',
                      display: 'flex',
                    }}
                  >
                    <EmailOutlinedIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      One-Time Email Link
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
                      Delivered directly to your registered email address on file.
                    </Typography>
                  </Box>
                </Stack>
              </Card>

              <Card
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#FFFFFF',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: '10px',
                      bgcolor: 'rgba(16, 185, 129, 0.2)',
                      color: '#34D399',
                      display: 'flex',
                    }}
                  >
                    <AccessTimeRoundedIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      60-Minute Expiry
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
                      Time-limited verification token protects against unauthorized access.
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Stack>
          </Box>

          {/* Bottom Security Footer */}
          <Box sx={{ position: 'relative', zIndex: 1, pt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#38BDF8' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>
                Mutual Trust End-to-End Cryptographic Security • MTC-FS
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Right Side: Form Panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 2.5, sm: 5, md: 8 },
            backgroundColor: 'background.paper',
            minHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>
            {/* Top Navigation */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, sm: 4 } }}>
              <Button
                component={RouterLink}
                to="/login"
                startIcon={<ArrowBackRoundedIcon />}
                size="small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
                }}
              >
                Back to Sign In
              </Button>

              <Chip
                label="Self-Service Portal"
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  borderColor: 'divider',
                  color: 'text.secondary',
                }}
              />
            </Stack>

            {/* Mobile Logo Display (visible on xs, hidden on md+) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box
                component="img"
                src="/assets/icons/mtc.png"
                alt="Mutual Trust Logo"
                sx={{ width: 42, height: 42, objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                  MUTUAL TRUST
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  COOPERATIVE SOCIETY
                </Typography>
              </Box>
            </Box>

            {submitted ? (
              /* Success Confirmation Card */
              <Box
                sx={{
                  textAlign: 'center',
                  p: { xs: 2.5, sm: 4 },
                  borderRadius: '16px',
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                  }}
                >
                  <MarkEmailReadRoundedIcon sx={{ fontSize: 36 }} />
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#071C2E' }}>
                  Check Your Inbox
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                  If an account with <strong>{identifier}</strong> exists, we have sent instructions and a secure reset link to the registered email address.
                </Typography>

                <Alert severity="info" sx={{ mb: 3, textAlign: 'left', borderRadius: '10px', fontSize: '0.85rem' }}>
                  The link will expire in <strong>60 minutes</strong>. Please check your inbox as well as your Spam or Junk folder.
                </Alert>

                <Stack spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.4,
                      borderRadius: '12px',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
                    }}
                  >
                    Return to Login
                  </Button>

                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setIdentifier('');
                    }}
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                  >
                    Request another link
                  </Button>
                </Stack>
              </Box>
            ) : (
              /* Request Form */
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      display: 'flex',
                    }}
                  >
                    <LockResetRoundedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#071C2E',
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Stack>

                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3.5, lineHeight: 1.6 }}>
                  No worries! Enter your registered <strong>email address</strong> or <strong>username</strong>, and we will send you a secure link to reset your password.
                </Typography>

                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
                    {errorMessage}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75, display: 'block' }}>
                        Email Address or Username
                      </Typography>
                      <TextField
                        fullWidth
                        size="medium"
                        placeholder="e.g. member@email.com or username"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        required
                        autoFocus
                        autoComplete="email"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: alpha(theme.palette.grey[500], 0.04),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.grey[500], 0.08),
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'background.paper',
                              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                            },
                          },
                        }}
                      />
                    </Box>

                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                      loadingIndicator={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress color="inherit" size={18} />
                          <Typography variant="button" sx={{ color: '#fff', fontSize: '0.9rem' }}>
                            Sending link...
                          </Typography>
                        </Stack>
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #0B3B60 0%, #0369A1 100%)',
                        boxShadow: '0 8px 20px rgba(11, 59, 96, 0.28)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #072740 0%, #0284C7 100%)',
                          boxShadow: '0 12px 28px rgba(11, 59, 96, 0.38)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Send Password Reset Link
                    </LoadingButton>

                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Remember your password?
                      </Typography>
                      <Button
                        component={RouterLink}
                        to="/login"
                        variant="text"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          textTransform: 'none',
                          color: 'primary.main',
                          p: 0,
                          minWidth: 'auto',
                          '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        Sign in
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
